import React, {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {motion} from 'framer-motion';
import {usePlanner} from '../context/PlannerContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiArrowLeft, FiArrowRight, FiUser, FiPlus, FiX} = FiIcons;

function QPSSurvey() {
  const navigate = useNavigate();
  const {state, dispatch} = usePlanner();
  
  // Initialize with empty students array, don't use state.students to avoid pre-filled data issues
  const [students, setStudents] = useState([createEmptyStudent()]);
  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);

  function createEmptyStudent() {
    return {
      name: '',
      // Section 1: Letter Names & Sounds
      letterNamesUppercase: '', // /26
      letterSoundsConsonants: '', // /21
      letterSoundsVowels: '', // /5
      
      // Section 2-7: Single Syllable Words
      vcCvcList: '', // /10
      vcCvcText: '', // /20
      cvccCcvcList: '', // /10
      cvccCcvcText: '', // /10
      silentEList: '', // /10
      silentEText: '', // /10
      rControlList: '', // /10
      rControlText: '', // /10
      consonantDigraphsList: '', // /10
      consonantDigraphsText: '', // /10
      vowelDigraphs: '', // /30
      
      // Section 8-12: Multi-Syllable Words
      prefixesSuffixes: '', // /30
      twoSyllable: '', // /10
      threeSyllable: '', // /10
      fourSyllable: '' // /10
    };
  }

  const handleInputChange = (field, value) => {
    const updatedStudents = [...students];
    updatedStudents[currentStudentIndex] = {...updatedStudents[currentStudentIndex], [field]: value};
    setStudents(updatedStudents);
  };

  const addStudent = () => {
    setStudents([...students, createEmptyStudent()]);
    setCurrentStudentIndex(students.length);
  };

  const deleteStudent = (indexToDelete) => {
    if (students.length === 1) return;
    const updatedStudents = students.filter((_, index) => index !== indexToDelete);
    setStudents(updatedStudents);
    if (indexToDelete === currentStudentIndex) {
      setCurrentStudentIndex(indexToDelete > 0 ? indexToDelete - 1 : 0);
    } else if (indexToDelete < currentStudentIndex) {
      setCurrentStudentIndex(currentStudentIndex - 1);
    }
  };

  const selectStudent = (index) => {
    setCurrentStudentIndex(index);
  };

  const processFormData = () => {
    const processedStudents = students.map(student => {
      // Calculate percentages from raw scores to match existing system
      const letterNamesScore = Math.round((parseInt(student.letterNamesUppercase) || 0) / 26 * 100);
      const letterSoundsScore = Math.round(((parseInt(student.letterSoundsConsonants) || 0) + (parseInt(student.letterSoundsVowels) || 0)) / 26 * 100);
      
      // Use the higher score from list vs text for each skill
      const vcCvcScore = Math.round(Math.max((parseInt(student.vcCvcList) || 0) / 10, (parseInt(student.vcCvcText) || 0) / 20) * 100);
      const cvccCcvcScore = Math.round(Math.max((parseInt(student.cvccCcvcList) || 0) / 10, (parseInt(student.cvccCcvcText) || 0) / 10) * 100);
      const cvceScore = Math.round(Math.max((parseInt(student.silentEList) || 0) / 10, (parseInt(student.silentEText) || 0) / 10) * 100);
      const rControlledScore = Math.round(Math.max((parseInt(student.rControlList) || 0) / 10, (parseInt(student.rControlText) || 0) / 10) * 100);
      const consonantDigraphsScore = Math.round(Math.max((parseInt(student.consonantDigraphsList) || 0) / 10, (parseInt(student.consonantDigraphsText) || 0) / 10) * 100);
      const vowelDigraphsScore = Math.round((parseInt(student.vowelDigraphs) || 0) / 30 * 100);
      const prefixesSuffixesScore = Math.round((parseInt(student.prefixesSuffixes) || 0) / 30 * 100);
      const twoSyllableScore = Math.round((parseInt(student.twoSyllable) || 0) / 10 * 100);
      const threeSyllableScore = Math.round((parseInt(student.threeSyllable) || 0) / 10 * 100);
      const fourSyllableScore = Math.round((parseInt(student.fourSyllable) || 0) / 10 * 100);

      return {
        name: student.name,
        letterNames: letterNamesScore,
        letterSounds: letterSoundsScore,
        vcCvc: vcCvcScore,
        cvccCcvc: cvccCcvcScore,
        cvce: cvceScore,
        rControlled: rControlledScore,
        consonantDigraphs: consonantDigraphsScore,
        vowelDigraphs: vowelDigraphsScore,
        prefixesSuffixes: prefixesSuffixesScore,
        twoSyllable: twoSyllableScore,
        threeSyllable: threeSyllableScore,
        fourSyllable: fourSyllableScore
      };
    }).filter(student => student.name.trim() !== '');

    dispatch({type: 'SET_ASSESSMENT_TYPE', payload: 'QPS'});
    dispatch({type: 'SET_STUDENTS', payload: processedStudents});
    navigate('/groups');
  };

  const isCurrentStudentValid = () => {
    const currentStudent = students[currentStudentIndex];
    return currentStudent.name.trim() && Object.values(currentStudent).some(value => value !== '' && value !== currentStudent.name);
  };

  const currentStudent = students[currentStudentIndex];

  return (
    <div className="min-h-screen">
      <motion.header
        initial={{opacity: 0, y: -20}}
        animate={{opacity: 1, y: 0}}
        className="bg-white shadow-sm border-b"
      >
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/assessment')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} />
              Back to Assessment Input
            </button>
            <h1 className="text-xl font-semibold text-thrive-slate font-display">Quick Phonics Screener (QPS)</h1>
            <div className="w-20"></div>
          </div>
        </div>
      </motion.header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{opacity: 0, y: 20}}
          animate={{opacity: 1, y: 0}}
          className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
        >
          <div className="bg-thrive-slate text-white px-6 py-4">
            <h2 className="text-xl font-bold text-center tracking-wider">Quick Phonics Screener—Record Form</h2>
          </div>

          <div className="p-6">
            {/* Student Tabs */}
            {students.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-gray-200">
                {students.map((student, index) => (
                  <div key={index} className={`relative px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    currentStudentIndex === index 
                      ? 'bg-thrive-teal text-white' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}>
                    <button onClick={() => selectStudent(index)} className="pr-6">
                      {student.name || `Student ${index + 1}`}
                    </button>
                    {students.length > 1 && (
                      <button
                        onClick={(e) => {e.stopPropagation(); deleteStudent(index);}}
                        className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-full transition-colors"
                        title="Delete student"
                      >
                        <SafeIcon icon={FiX} className="text-xs" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Student Info */}
            <div className="mb-8 pb-6 border-b border-gray-200">
              <div className="max-w-md">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <SafeIcon icon={FiUser} className="inline mr-1" />
                  Student Name
                </label>
                <input
                  type="text"
                  value={currentStudent.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                  placeholder="Enter student name"
                />
              </div>
            </div>

            {/* Section 1: Letter Names & Sounds */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-thrive-slate mb-4 tracking-wider">Section 1: Letter Names & Sounds (K-1st)</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="26"
                      value={currentStudent.letterNamesUppercase}
                      onChange={(e) => handleInputChange('letterNamesUppercase', e.target.value)}
                      className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/26</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">(a) Names</span>
                    <span>Letter names—uppercase & lowercase</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="21"
                      value={currentStudent.letterSoundsConsonants}
                      onChange={(e) => handleInputChange('letterSoundsConsonants', e.target.value)}
                      className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/21</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">(b) Sounds</span>
                    <span>Consonant sounds</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="5"
                      value={currentStudent.letterSoundsVowels}
                      onChange={(e) => handleInputChange('letterSoundsVowels', e.target.value)}
                      className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/5</div>
                  </div>
                  <div className="flex-1 ml-8">
                    <span>Vowel sounds</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2-7: Single Syllable Words */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-thrive-slate mb-4 tracking-wider">Section 2-7: Single Syllable Words</h3>
              <div className="space-y-6">
                
                {/* VC & CVC */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-thrive-slate mb-3">2. vc & cvc (Gr. 1)</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 text-center">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          value={currentStudent.vcCvcList}
                          onChange={(e) => handleInputChange('vcCvcList', e.target.value)}
                          className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                        />
                        <div className="text-xs text-gray-500 mt-1">/10</div>
                      </div>
                      <span className="text-sm">(a) in List</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-16 text-center">
                        <input
                          type="number"
                          min="0"
                          max="20"
                          value={currentStudent.vcCvcText}
                          onChange={(e) => handleInputChange('vcCvcText', e.target.value)}
                          className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                        />
                        <div className="text-xs text-gray-500 mt-1">/20</div>
                      </div>
                      <span className="text-sm">(b) in Text</span>
                    </div>
                  </div>
                </div>

                {/* CVCC & CCVC */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-thrive-slate mb-3">3. cvcc & ccvc (Gr. 1)</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 text-center">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          value={currentStudent.cvccCcvcList}
                          onChange={(e) => handleInputChange('cvccCcvcList', e.target.value)}
                          className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                        />
                        <div className="text-xs text-gray-500 mt-1">/10</div>
                      </div>
                      <span className="text-sm">(a) in List</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-16 text-center">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          value={currentStudent.cvccCcvcText}
                          onChange={(e) => handleInputChange('cvccCcvcText', e.target.value)}
                          className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                        />
                        <div className="text-xs text-gray-500 mt-1">/10</div>
                      </div>
                      <span className="text-sm">(b) in Text</span>
                    </div>
                  </div>
                </div>

                {/* Silent e CVC-e */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-thrive-slate mb-3">4. Silent e CVC-e (Gr. 1-2)</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 text-center">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          value={currentStudent.silentEList}
                          onChange={(e) => handleInputChange('silentEList', e.target.value)}
                          className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                        />
                        <div className="text-xs text-gray-500 mt-1">/10</div>
                      </div>
                      <span className="text-sm">(a) in List</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-16 text-center">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          value={currentStudent.silentEText}
                          onChange={(e) => handleInputChange('silentEText', e.target.value)}
                          className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                        />
                        <div className="text-xs text-gray-500 mt-1">/10</div>
                      </div>
                      <span className="text-sm">(b) in Text</span>
                    </div>
                  </div>
                </div>

                {/* R-Control Vowels */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-thrive-slate mb-3">5. R-Control Vowels (Gr. 1-2)</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 text-center">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          value={currentStudent.rControlList}
                          onChange={(e) => handleInputChange('rControlList', e.target.value)}
                          className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                        />
                        <div className="text-xs text-gray-500 mt-1">/10</div>
                      </div>
                      <span className="text-sm">(a) in List</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-16 text-center">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          value={currentStudent.rControlText}
                          onChange={(e) => handleInputChange('rControlText', e.target.value)}
                          className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                        />
                        <div className="text-xs text-gray-500 mt-1">/10</div>
                      </div>
                      <span className="text-sm">(b) in Text</span>
                    </div>
                  </div>
                </div>

                {/* Consonant Digraphs */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-thrive-slate mb-3">6. Consonant Digraphs (Gr. 1-3)</h4>
                  <div className="text-xs text-gray-600 mb-2">th, ng, sh, wh, ing, ck, kn, wr, nk</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-16 text-center">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          value={currentStudent.consonantDigraphsList}
                          onChange={(e) => handleInputChange('consonantDigraphsList', e.target.value)}
                          className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                        />
                        <div className="text-xs text-gray-500 mt-1">/10</div>
                      </div>
                      <span className="text-sm">(a) in List</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-16 text-center">
                        <input
                          type="number"
                          min="0"
                          max="10"
                          value={currentStudent.consonantDigraphsText}
                          onChange={(e) => handleInputChange('consonantDigraphsText', e.target.value)}
                          className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                        />
                        <div className="text-xs text-gray-500 mt-1">/10</div>
                      </div>
                      <span className="text-sm">(b) in Text</span>
                    </div>
                  </div>
                </div>

                {/* Vowel Digraphs & Diphthongs */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-thrive-slate mb-3">7. Vowel Digraphs & Diphthongs (Gr. 1-3)</h4>
                  <div className="text-xs text-gray-600 mb-2">oa, ea, oo, ee, ai, ol, ay, ou, oi, oy, au, aw, oe, ew, ow</div>
                  <div className="flex items-center gap-4">
                    <div className="w-16 text-center">
                      <input
                        type="number"
                        min="0"
                        max="30"
                        value={currentStudent.vowelDigraphs}
                        onChange={(e) => handleInputChange('vowelDigraphs', e.target.value)}
                        className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                      />
                      <div className="text-xs text-gray-500 mt-1">/30</div>
                    </div>
                    <span className="text-sm">Word List Score</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 8-12: Multi-Syllable Words */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-thrive-slate mb-4 tracking-wider">Section 8-12: Multi-Syllable Words</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={currentStudent.prefixesSuffixes}
                      onChange={(e) => handleInputChange('prefixesSuffixes', e.target.value)}
                      className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/30</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">8/9.</span>
                    <span>Prefixes & Suffixes (Gr. 2, 3, 4-6)</span>
                    <div className="text-xs text-gray-600 mt-1">dis-, non-, in-, re-, -tion, -ous, -ness, -ion, -able, -est, -ie, -ful, -ic, be-, de-</div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={currentStudent.twoSyllable}
                      onChange={(e) => handleInputChange('twoSyllable', e.target.value)}
                      className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/10</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">10.</span>
                    <span>2-Syllable Words (Gr. 2, 3, 4-6)</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={currentStudent.threeSyllable}
                      onChange={(e) => handleInputChange('threeSyllable', e.target.value)}
                      className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/10</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">11.</span>
                    <span>3-Syllable Words (Gr. 2, 3, 4-6)</span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-16 text-center">
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={currentStudent.fourSyllable}
                      onChange={(e) => handleInputChange('fourSyllable', e.target.value)}
                      className="w-full px-2 py-2 border border-gray-300 rounded text-center focus:ring-2 focus:ring-thrive-teal focus:border-transparent"
                    />
                    <div className="text-xs text-gray-500 mt-1">/10</div>
                  </div>
                  <div className="flex-1">
                    <span className="font-medium mr-2">12.</span>
                    <span>4-Syllable Words (Gr. 2, 3, 4-6)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button
                onClick={addStudent}
                className="bg-thrive-mint hover:bg-thrive-teal text-thrive-slate hover:text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-md"
              >
                <SafeIcon icon={FiPlus} />
                Add Student
              </button>
              <button
                onClick={processFormData}
                disabled={!isCurrentStudentValid()}
                className="bg-thrive-teal hover:bg-thrive-slate disabled:bg-gray-300 text-white px-8 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 shadow-lg disabled:shadow-none"
              >
                Continue to Groups
                <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

export default QPSSurvey;